import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Ship, Download, Plus } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const COMMODITIES = ['bulk_commodities', 'general_merchandise', 'foodstuffs_ambient', 'machinery_equipment', 'refrigerated_goods', 'electronics', 'fragile_glass_ceramics', 'personal_effects', 'high_theft_goods', 'project_cargo'];
const ROUTES = ['domestic_only', 'aus_nz', 'established_international', 'high_risk_lanes'];
const pretty = s => String(s || '').replace(/_/g, ' ');

const EMPTY = { consignment_ref: '', description: '', commodity_category: '', origin: '', destination: '', route: '', conveyance: 'sea', insured_value: '', sailing_date: '', war_srcc: false };

export const OpenCoverPanel = ({ api, policy }) => {
  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/marine/open-cover/${policy.policy_id}/declarations`)
    .then(r => setData(r.data)).catch(() => {}), [api, policy.policy_id]);
  useEffect(() => { load(); }, [load]);

  const declare = async () => {
    if (!form.origin || !form.destination || !Number(form.insured_value)) { toast.error('Origin, destination and insured value are required'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marine/open-cover/${policy.policy_id}/declarations`, {
        ...form, insured_value: Number(form.insured_value),
        commodity_category: form.commodity_category || null, route: form.route || null,
        consignment_ref: form.consignment_ref || null,
      });
      if (r.data.status === 'declined') {
        toast.error((r.data.declined_reasons || [])[0] || 'Declined — sanctions embargo');
      } else {
        toast.success(r.data.status === 'accepted'
          ? `Accepted — Certificate ${r.data.certificate_number} issued, premium $${r.data.premium.toLocaleString()}`
          : 'Declared — held for underwriter review');
      }
      setForm(EMPTY); setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Declaration failed');
    } finally { setBusy(false); }
  };

  const approve = async (id) => {
    try {
      const r = await api.post(`/marine/declarations/${id}/approve`);
      toast.success(`Released — Certificate ${r.data.certificate_number} issued`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  const inp = (field, ph, extra = {}) => (
    <input value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
      placeholder={ph} data-testid={`decl-${field}-input`}
      className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white" {...extra} />
  );

  return (
    <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(0,180,216,0.25)' }} data-testid="open-cover-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2">
          <Ship className="w-4 h-4 mt-0.5" style={{ color: C.teal }} />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Open Cover — Shipment Declarations</h3>
            <p className="text-[11px]" style={{ color: C.slate }}>Each consignment is declared, rated against the open cover and certificated. Certificates are negotiable documents.</p>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="marine-declare-toggle-btn"
          className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium text-white" style={{ background: C.teal }}>
          <Plus className="w-3.5 h-3.5" /> Declare Shipment
        </button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-4 text-[11px]" data-testid="open-cover-totals" style={{ color: C.slate }}>
          <span>Declarations <strong style={{ color: C.navy }}>{data.totals.count}</strong></span>
          <span>Accepted <strong style={{ color: C.green }}>{data.totals.accepted}</strong></span>
          <span>Held <strong style={{ color: C.amber }}>{data.totals.held}</strong></span>
          {(data.totals.declined || 0) > 0 && <span>Declined <strong style={{ color: C.red }} data-testid="open-cover-declined-count">{data.totals.declined}</strong></span>}
          <span>Declared value <strong style={{ color: C.navy }}>${data.totals.declared_value.toLocaleString()}</strong></span>
          <span>Declared premium <strong style={{ color: C.navy }}>${data.totals.premium.toLocaleString()}</strong></span>
        </div>
      )}

      {showForm && (
        <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(0,180,216,0.05)' }} data-testid="marine-declare-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {inp('consignment_ref', 'Consignment ref (optional)')}
            {inp('description', 'Description of goods')}
            <select value={form.commodity_category} onChange={e => setForm(p => ({ ...p, commodity_category: e.target.value }))}
              data-testid="decl-commodity-select" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white">
              <option value="">Commodity — per open cover</option>
              {COMMODITIES.map(c => <option key={c} value={c}>{pretty(c)}</option>)}
            </select>
            <select value={form.conveyance} onChange={e => setForm(p => ({ ...p, conveyance: e.target.value }))}
              data-testid="decl-conveyance-select" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white">
              {['sea', 'air', 'road', 'rail'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {inp('origin', 'Origin (port/city)')}
            {inp('destination', 'Destination (port/city)')}
            <select value={form.route} onChange={e => setForm(p => ({ ...p, route: e.target.value }))}
              data-testid="decl-route-select" className="px-2 py-1.5 border border-input rounded-sm text-xs bg-white">
              <option value="">Route — per open cover</option>
              {ROUTES.map(r => <option key={r} value={r}>{pretty(r)}</option>)}
            </select>
            {inp('insured_value', 'Insured value $ (CIF + uplift)', { type: 'number' })}
            {inp('sailing_date', 'Sailing date', { type: 'date' })}
            <label className="flex items-center gap-1.5 text-xs" style={{ color: C.slate }}>
              <input type="checkbox" checked={form.war_srcc} data-testid="decl-war-checkbox"
                onChange={e => setForm(p => ({ ...p, war_srcc: e.target.checked }))} /> War & SRCC
            </label>
          </div>
          <button onClick={declare} disabled={busy} data-testid="marine-declare-submit-btn"
            className="px-4 py-1.5 rounded-sm text-xs font-medium text-white disabled:opacity-40" style={{ background: C.navy }}>
            {busy ? 'Rating…' : 'Declare & Rate'}
          </button>
        </div>
      )}

      {data && data.declarations.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left" style={{ color: C.slate }}>
              <th className="py-1.5 pr-2 font-medium">Ref</th><th className="py-1.5 pr-2 font-medium">Voyage</th>
              <th className="py-1.5 pr-2 font-medium">Value</th><th className="py-1.5 pr-2 font-medium">Rate</th>
              <th className="py-1.5 pr-2 font-medium">Premium</th><th className="py-1.5 pr-2 font-medium">Status</th>
              <th className="py-1.5 font-medium">Certificate</th>
            </tr></thead>
            <tbody>
              {data.declarations.map(d => (
                <tr key={d.declaration_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }} data-testid={`marine-decl-row-${d.consignment_ref}`}>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: C.blue }}>{d.consignment_ref}</td>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>{d.origin} → {d.destination} <span style={{ color: C.slate }}>({d.conveyance})</span></td>
                  <td className="py-1.5 pr-2 font-mono">${(d.insured_value || 0).toLocaleString()}</td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: C.slate }}>{d.effective_rate_pct}%</td>
                  <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>${(d.premium || 0).toLocaleString()}</td>
                  <td className="py-1.5 pr-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" title={[...(d.declined_reasons || []), ...(d.held_reasons || [])].join('; ')}
                      style={{
                        background: d.status === 'accepted' ? 'rgba(29,158,117,0.1)' : d.status === 'declined' ? 'rgba(226,75,74,0.12)' : 'rgba(232,160,32,0.12)',
                        color: d.status === 'accepted' ? C.green : d.status === 'declined' ? C.red : '#A8720E',
                      }}>
                      {d.status}
                    </span>
                  </td>
                  <td className="py-1.5">
                    {d.status === 'accepted' ? (
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-[10px]" style={{ color: C.navy }}>{d.certificate_number}</span>
                        <a href={`${API_BASE}/marine/declarations/${d.declaration_id}/certificate.pdf`} target="_blank" rel="noreferrer"
                          data-testid={`marine-cert-btn-${d.consignment_ref}`} title="Certificate of Insurance (negotiable)">
                          <Download className="w-3.5 h-3.5" style={{ color: C.teal }} />
                        </a>
                        <a href={`${API_BASE}/marine/declarations/${d.declaration_id}/acceptance.pdf`} target="_blank" rel="noreferrer"
                          className="text-[10px] underline" style={{ color: C.slate }} data-testid={`marine-acceptance-btn-${d.consignment_ref}`}>advice</a>
                      </span>
                    ) : d.status === 'declined' ? (
                      <span className="text-[10px] font-medium" style={{ color: C.red }} data-testid={`marine-declined-label-${d.consignment_ref}`}>
                        Embargo — not releasable
                      </span>
                    ) : (
                      <button onClick={() => approve(d.declaration_id)} data-testid={`marine-approve-btn-${d.consignment_ref}`}
                        className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(232,160,32,0.12)', color: '#A8720E' }}>
                        Release & Certificate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.declarations.length === 0 && (
        <p className="text-xs py-2 text-center" style={{ color: C.slate }} data-testid="open-cover-empty">
          No consignments declared under this open cover yet.
        </p>
      )}
    </Card>
  );
};

export default OpenCoverPanel;
